/**
 * La fecha del examen y su sala, dentro de la tarjeta del proceso (paso de tipo fecha).
 *
 * Examen de Título no se entrega ni se evalúa: los integrantes —guía, coguía y la
 * comisión del Informe— coordinan un rango de 90 minutos (lo hará Miti) y, con todos
 * coordinados, el estudiante pide la sala en SAR. Nadie elige fecha ni sala: **SAR elige
 * la sala**. Si no hay, se avisa que hay que reagendar y la coordinación vuelve a empezar.
 * Reprogramar es coordinar otro rango y volver a agendar.
 *
 * La coordinación es en **Miti**: el estudiante —o la coordinación, desde la ficha del
 * estudiante— crea ahí la agenda («Coordinar en Miti», con un enlace firmado a su nombre) y la
 * comparte con la comisión; cuando se confirma el horario, Titulación lo lee (al abrir la
 * tarjeta, con «Actualizar desde Miti» y por cron) y, si todos van presenciales, agenda sola.
 * Con remotos, el estudiante agrega el enlace y confirma. El estudiante **o la coordinación**
 * pueden crear y coordinar la agenda y agendar o cancelar la sala en SAR; cuando lo hace la
 * coordinación, al estudiante le llega un correo. Guía y comisión ven solo la fecha y la sala.
 */
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  EventAvailableOutlined as AgendarIcon,
  EventBusyOutlined as CancelarIcon,
  SyncOutlined as RevisarIcon,
  OpenInNew as AbrirIcon,
  GroupsOutlined as MitiIcon,
  AttachFileOutlined as AdjuntarIcon,
} from "@mui/icons-material";
import EstadoChip from "../kit/EstadoChip";
import { InstanciaPaso } from "../../types/instanciaPaso";
import { CartaRemota, Coordinacion, Defensa, IntegranteCoordinacion } from "../../types/defensa";
import {
  useAgendarDefensa,
  useAprobarCartaRemota,
  useCancelarDefensa,
  useIngresarCorreo,
  useRevisarDefensa,
  useSincronizarMiti,
  useSubirCartaRemota,
} from "../../queries/useDefensas";
import { defensaService } from "../../services/defensaService";
import { mensajeDeError } from "../../utils/errores";

const pad = (n: number) => String(n).padStart(2, "0");
const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const horaInput = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** «martes 27 de octubre, 10:00–11:30». */
function cuando(inicio: string, termino: string) {
  const a = new Date(inicio);
  const b = new Date(termino);
  return `${DIAS[a.getDay()]} ${a.getDate()} de ${MESES[a.getMonth()]}, ${horaInput(a)}–${horaInput(b)}`;
}


const ESTADO_TONO = { no_coordinado: "aviso", coordinado: "info", agendado: "exito" } as const;
const clave = (i: Coordinacion["integrantes"][number]) => String(i.persona_id ?? i.nombre);

const RESULTADO: Record<string, string> = {
  creada: "Sala agendada",
  movida: "Defensa reprogramada",
  actualizada: "Datos actualizados (misma hora y sala)",
  sin_cambios: "Sin cambios: SAR ya tenía exactamente esto",
};

/** «20 oct». */
const diaCorto = (iso: string) => {
  const d = new Date(iso);
  return `${d.getDate()} ${MESES[d.getMonth()].slice(0, 3)}`;
};

const EN_HORARIO = {
  puede: { texto: "✓ puede en el horario", color: "success.main" },
  quizas: { texto: "≈ quizás puede en el horario", color: "warning.main" },
  no_puede: { texto: "✗ no puede en el horario", color: "error.main" },
} as const;

/** En qué va un integrante: respondió (y si puede en el horario) o qué se le avisó. */
function estadoIntegrante(i: IntegranteCoordinacion): { texto: string; color: string } {
  if (i.respondio_en) {
    if (i.en_horario) return EN_HORARIO[i.en_horario];
    return { texto: `✓ respondió el ${diaCorto(i.respondio_en)}`, color: "success.main" };
  }
  const a = i.aviso;
  if (a?.sin_correo) return { texto: "sin responder · no tiene correo", color: "error.main" };
  if (a?.invitado_en) {
    const recordatorios = a.recordatorios ? ` · ${a.recordatorios} recordatorio${a.recordatorios === 1 ? "" : "s"}` : "";
    return { texto: `sin responder · avisado el ${diaCorto(a.invitado_en)}${recordatorios}`, color: "warning.main" };
  }
  return { texto: "sin responder", color: "warning.main" };
}

/** Ingresar el correo de quien no tiene: queda en esta coordinación y se le invita al tiro. */
function AgregarCorreo({ it, i }: { it: InstanciaPaso; i: IntegranteCoordinacion }) {
  const ingresar = useIngresarCorreo();
  const [abierto, setAbierto] = useState(false);
  const [email, setEmail] = useState("");
  if (!abierto) {
    return (
      <Button size="small" variant="text" sx={{ ml: 0.5, py: 0, minWidth: 0 }} onClick={() => setAbierto(true)}>
        Agregar correo
      </Button>
    );
  }
  const guardar = () =>
    ingresar.mutate({ id: it.id, clave: clave(i), email: email.trim() }, { onSuccess: () => setAbierto(false) });
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 0.75, mb: 0.5, alignItems: "flex-start" }}>
      <TextField
        size="small"
        type="email"
        label={`Correo de ${i.nombre}`}
        value={email}
        autoFocus
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && email.trim() && guardar()}
        error={ingresar.isError}
        helperText={ingresar.isError ? mensajeDeError(ingresar.error, "No se pudo guardar.") : "Le llegará su enlace personal a la agenda."}
        sx={{ minWidth: 260 }}
      />
      <Button size="small" variant="contained" disabled={!email.trim() || ingresar.isPending} onClick={guardar}>
        {ingresar.isPending ? "Guardando…" : "Guardar y avisar"}
      </Button>
      <Button size="small" onClick={() => setAbierto(false)} disabled={ingresar.isPending}>
        Cancelar
      </Button>
    </Stack>
  );
}

/** Quiénes van, cómo asisten y en qué va cada uno con la fecha. Quien gestiona puede
 * ingresar el correo de quien no tiene; la coordinación, además, coordinar a un integrante en su
 * nombre (entrando con su enlace personal de Miti) y justificar la modalidad remota de quien
 * quedó remoto (sin eso no se agenda). */
function Integrantes({ c, it, editable, esCoordinador = false }: { c: Coordinacion; it: InstanciaPaso; editable: boolean; esCoordinador?: boolean }) {
  if (!c.integrantes.length) return null;
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
      {c.integrantes.map((i) => {
        const e = estadoIntegrante(i);
        return (
          <Typography component="li" variant="body2" key={clave(i)}>
            {i.nombre} · {i.rol}
            {i.respondio_en ? ` · ${i.asistencia}` : ""}
            <Typography component="span" variant="caption" sx={{ ml: 0.75, color: e.color }}>
              · {e.texto}
            </Typography>
            {i.aviso?.correo && !i.respondio_en && (
              <Typography component="span" variant="caption" color="text.secondary">
                {" "}
                · correo ingresado: {i.aviso.correo}
              </Typography>
            )}
            {editable && i.aviso?.sin_correo && !i.respondio_en && <AgregarCorreo it={it} i={i} />}
            {esCoordinador && !i.respondio_en && i.enlace && <CoordinarIntegrante nombre={i.nombre} enlace={i.enlace} />}
            {esCoordinador && i.asistencia === "remoto" && i.carta && (
              <GestionCarta it={it} clave={clave(i)} nombre={i.nombre} carta={i.carta} />
            )}
          </Typography>
        );
      })}
      {esCoordinador && c.estudiante?.asistencia === "remoto" && c.estudiante.carta && (
        <Typography component="li" variant="body2" key="estudiante">
          {c.estudiante.nombre} · estudiante · remoto
          <GestionCarta it={it} clave={c.estudiante.clave} nombre={c.estudiante.nombre} carta={c.estudiante.carta} />
        </Typography>
      )}
    </Box>
  );
}

/** Para la coordinación: coordinar a un integrante **en su nombre**, entrando con su enlace
 * personal de Miti (el mismo que se le envía por correo). No pide justificativo —eso es solo para
 * la modalidad remota—; aparece mientras no haya respondido. Antes de abrir Miti advierte dos
 * cosas: lo que se marque quedará como la disponibilidad de esa persona, y que si la coordinación
 * entró a Miti con su propia cuenta VTI, Miti **no la deja** responder por otra persona (protección
 * de Miti; no se toca) y hay que abrir el enlace en una **ventana privada**. El aviso vive acá. */
function CoordinarIntegrante({ nombre, enlace }: { nombre: string; enlace: string }) {
  const [confirmar, setConfirmar] = useState(false);
  return (
    <>
      <Button
        size="small"
        variant="text"
        endIcon={<AbrirIcon fontSize="small" />}
        sx={{ display: "block", py: 0, minWidth: 0, mt: 0.25 }}
        onClick={() => setConfirmar(true)}
      >
        Coordinar a {nombre} en Miti
      </Button>
      {confirmar && (
        <Dialog open onClose={() => setConfirmar(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Ingresar la disponibilidad de {nombre}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Vas a ingresar la disponibilidad de {nombre} <strong>en su nombre</strong>. Lo que marques
              quedará registrado como la disponibilidad de {nombre}, no la tuya.
            </DialogContentText>
            <Alert severity="info">
              Si entraste a Miti con tu cuenta, no te dejará responder por otra persona. Abre este enlace
              en una <strong>ventana privada</strong> (clic derecho en «Entrar como {nombre}» → «Abrir
              en ventana de incógnito»): ahí Miti te deja entrar como {nombre}.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmar(false)}>Volver</Button>
            <Button
              variant="contained"
              color="warning"
              href={enlace}
              target="_blank"
              rel="noreferrer"
              endIcon={<AbrirIcon fontSize="small" />}
              onClick={() => setConfirmar(false)}
            >
              Entrar como {nombre}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

/** Para la coordinación: la **carta de modalidad remota** de un participante (comisión o estudiante)
 * que quedó remoto. Las defensas son presenciales por defecto; sin la carta firmada **aprobada** no
 * se agenda con esa persona. Descarga la carta tipo de su rol (si ya está cargada), sube la firmada
 * y, como coordinación, la aprueba. La regla vive en Titulación, no en Miti. */
function GestionCarta({ it, clave, nombre, carta }: { it: InstanciaPaso; clave: string; nombre: string; carta: CartaRemota }) {
  const subir = useSubirCartaRemota();
  const aprobar = useAprobarCartaRemota();
  const entrada = useRef<HTMLInputElement>(null);
  const ocupado = subir.isPending || aprobar.isPending;

  const elegirArchivo = (e: ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (archivo) subir.mutate({ id: it.id, clave, archivo });
  };

  return (
    <Box sx={{ mt: 0.5, mb: 0.5 }}>
      {carta.estado === "sin_carta" && (
        <Typography variant="caption" color="warning.main" display="block">
          Remoto sin carta · no se puede agendar hasta cargarla y aprobarla
        </Typography>
      )}
      {carta.estado === "cargada" && (
        <Typography variant="caption" color="warning.main" display="block">
          Carta cargada{carta.nombre ? ` (${carta.nombre})` : ""} · falta aprobarla para poder agendar
        </Typography>
      )}
      {carta.estado === "aprobada" && (
        <Typography variant="caption" color="text.secondary" display="block">
          Remoto · carta aprobada{carta.aprobado_por ? ` por ${carta.aprobado_por}` : ""}
          {carta.aprobado_en ? ` el ${diaCorto(carta.aprobado_en)}` : ""}
        </Typography>
      )}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        {carta.plantilla && (
          <Button
            size="small"
            variant="text"
            sx={{ py: 0, minWidth: 0 }}
            href={defensaService.urlPlantillaCarta(it.id, clave)}
            target="_blank"
            rel="noreferrer"
            endIcon={<AbrirIcon fontSize="small" />}
          >
            Descargar carta
          </Button>
        )}
        {carta.estado !== "sin_carta" && (
          <Button
            size="small"
            variant="text"
            sx={{ py: 0, minWidth: 0 }}
            href={defensaService.urlCartaFirmada(it.id, clave)}
            target="_blank"
            rel="noreferrer"
            endIcon={<AbrirIcon fontSize="small" />}
          >
            Ver carta firmada
          </Button>
        )}
        <Button
          size="small"
          variant="text"
          sx={{ py: 0, minWidth: 0 }}
          startIcon={<AdjuntarIcon fontSize="small" />}
          disabled={ocupado}
          onClick={() => entrada.current?.click()}
        >
          {carta.estado === "sin_carta" ? "Subir carta firmada" : "Subir otra"}
        </Button>
        {carta.estado === "cargada" && (
          <Button
            size="small"
            variant="contained"
            color="warning"
            sx={{ py: 0 }}
            disabled={ocupado}
            onClick={() => aprobar.mutate({ id: it.id, clave })}
          >
            Aprobar
          </Button>
        )}
      </Stack>
      <input hidden type="file" ref={entrada} onChange={elegirArchivo} />
      {(subir.isError || aprobar.isError) && (
        <Alert severity="error" sx={{ mt: 0.5 }}>
          {mensajeDeError(subir.error || aprobar.error, `No se pudo guardar la carta de ${nombre}.`)}
        </Alert>
      )}
    </Box>
  );
}

/** Pedir (o mover) la sala para el rango coordinado. Solo se agregan enlace y notas. */
function DialogoAgendar({ it, onClose, onListo }: { it: InstanciaPaso; onClose: () => void; onListo: (msg: string) => void }) {
  const agendar = useAgendarDefensa();
  const c = it.coordinacion as Coordinacion;
  const d = it.defensa?.estado === "agendada" ? it.defensa : null;
  const [f, setF] = useState({ enlace_remoto: d?.enlace_remoto ?? "", notas: d?.notas ?? "" });
  const hayRemotos = c.integrantes.some((i) => i.asistencia === "remoto");
  const respuesta = (agendar.error as AxiosError<{ detail?: string; errores?: Record<string, string> }>)?.response;
  const sinSala = respuesta?.status === 409;
  const errores = respuesta?.status === 422 ? respuesta.data?.errores ?? {} : {};

  const enviar = () =>
    agendar.mutate(
      { id: it.id, pedido: f },
      {
        onSuccess: (r) => {
          const nueva = r.proceso.defensa;
          onListo(`${RESULTADO[r.resultado] ?? "Listo"}${nueva ? `: ${nueva.sala}, ${cuando(nueva.inicio, nueva.termino)}` : ""}.`);
          onClose();
        },
      },
    );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{d ? "Mover la defensa en SAR" : "Agendar la defensa en SAR"}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5 }}>
          Titulación pide la sala para el horario que coordinaron todos. Si no hay salas disponibles en ese horario, habrá que
          reagendar.
          {d && ` Hoy está en ${d.sala}, ${cuando(d.inicio, d.termino)}.`}
        </DialogContentText>
        {c.inicio && c.termino && (
          <Typography variant="body2" fontWeight={600}>
            Horario coordinado: {cuando(c.inicio, c.termino)}
          </Typography>
        )}
        <Integrantes c={c} it={it} editable={false} />
        {hayRemotos && (
          <TextField
            label="Enlace de la videollamada"
            size="small"
            fullWidth
            value={f.enlace_remoto}
            onChange={(e) => setF({ ...f, enlace_remoto: e.target.value })}
            error={!!errores.enlace_remoto}
            helperText={errores.enlace_remoto}
            sx={{ mt: 2 }}
          />
        )}
        <TextField
          label="Notas para la sala"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={f.notas}
          onChange={(e) => setF({ ...f, notas: e.target.value })}
          sx={{ mt: 2 }}
        />
        {sinSala && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              No hay salas disponibles en ese horario: hay que reagendar.
            </Typography>
            <Typography variant="body2">
              {d ? `La defensa sigue en ${d.sala}, ${cuando(d.inicio, d.termino)}. ` : "No se reservó nada. "}
              La coordinación vuelve a empezar.
            </Typography>
          </Alert>
        )}
        {agendar.isError && !sinSala && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mensajeDeError(agendar.error, "No se pudo agendar en SAR.")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={agendar.isPending}>
          Cerrar
        </Button>
        {!sinSala && (
          <Button variant="contained" onClick={enviar} disabled={agendar.isPending}>
            {agendar.isPending ? "Consultando SAR…" : d ? "Mover en SAR" : "Pedir sala en SAR"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function DialogoCancelar({ it, onClose, onListo }: { it: InstanciaPaso; onClose: () => void; onListo: (msg: string) => void }) {
  const cancelar = useCancelarDefensa();
  const [motivo, setMotivo] = useState("");
  const d = it.defensa as Defensa;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cancelar la defensa</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Se libera {d.sala} el {cuando(d.inicio, d.termino)}. En SAR queda registro de la cancelación con el motivo.
        </DialogContentText>
        <TextField label="Motivo" size="small" fullWidth multiline minRows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        {cancelar.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mensajeDeError(cancelar.error, "No se pudo cancelar en SAR.")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={cancelar.isPending}>
          Volver
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={cancelar.isPending}
          onClick={() =>
            cancelar.mutate(
              { id: it.id, motivo },
              {
                onSuccess: () => {
                  onListo("Defensa cancelada: la sala quedó libre en SAR.");
                  onClose();
                },
              },
            )
          }
        >
          {cancelar.isPending ? "Cancelando…" : "Cancelar la defensa"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Nueva agenda cuando la actual ya tiene respuestas: se avisa qué se pierde antes de abrir Miti. */
function DialogoNuevaAgenda({
  respondieron,
  onClose,
  onConfirmar,
}: {
  respondieron: IntegranteCoordinacion[];
  onClose: () => void;
  onConfirmar: () => void;
}) {
  const n = respondieron.length;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Crear una nueva agenda</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5 }}>
          La agenda actual tiene {n} integrante{n === 1 ? "" : "s"} que ya respondi{n === 1 ? "ó" : "eron"}:
        </DialogContentText>
        <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
          {respondieron.map((i) => (
            <Typography component="li" variant="body2" key={clave(i)}>
              {i.nombre} · {i.rol}
            </Typography>
          ))}
        </Box>
        <DialogContentText>
          Al crear la nueva agenda en Miti, la actual se eliminará con sus respuestas. A quienes ya respondieron les
          avisaremos por correo, si lo tenemos, para que vuelvan a indicar su disponibilidad.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Volver</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => {
            onClose();
            onConfirmar();
          }}
        >
          Crear nueva agenda
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type AvisoDefensa = { tono: "success" | "info" | "warning"; texto: string };

/**
 * `coordinacion`: quien mira es el coordinador, desde la ficha del estudiante. Ve el
 * detalle, puede crear y coordinar la agenda en Miti, revisarla contra SAR y —como el
 * estudiante— agendar o cancelar la sala por él (`it.puede_agendar_defensa`, que decide el
 * backend; al estudiante le avisa por correo). Crear/coordinar en Miti va con `gestion`
 * (estudiante o coordinador); agendar y cancelar en SAR, con `agenda`, que ahora ambos tienen.
 */
export default function DefensaSala({ it, coordinacion: esCoordinador }: { it: InstanciaPaso; coordinacion: boolean }) {
  const [dialogo, setDialogo] = useState<"agendar" | "cancelar" | "nueva" | null>(null);
  const [aviso, setAviso] = useState<AvisoDefensa | null>(null);
  const revisar = useRevisarDefensa();
  const sincronizar = useSincronizarMiti();
  const [abriendoMiti, setAbriendoMiti] = useState(false);
  const d = it.defensa;
  const c = it.coordinacion;
  const agendada = d?.estado === "agendada";
  const agenda = it.puede_agendar_defensa;
  const gestion = agenda || esCoordinador;
  const estado = it.estado_paso;
  const texto = useMemo(() => (d ? cuando(d.inicio, d.termino) : ""), [d]);
  // Al abrir la tarjeta se lee Miti una vez: lo acordado allá aparece sin esperar al cron.
  const leido = useRef(false);
  const conMiti = !!c?.miti.configurado && (agenda || esCoordinador);
  useEffect(() => {
    if (conMiti && !leido.current && it.estado_paso?.codigo !== "agendado") {
      leido.current = true;
      sincronizar.mutate(it.id);
    }
  }, [conMiti, it.id, it.estado_paso?.codigo, sincronizar]);

  if (it.paso_tipo !== "fecha" || !c) return null;
  // Guía y comisión: solo una sala vigente.
  if (!gestion && !agendada) return null;

  const abrirMiti = async () => {
    setAbriendoMiti(true);
    try {
      window.open(await defensaService.enlaceMiti(it.id), "_blank", "noopener");
    } catch (e) {
      setAviso({ tono: "warning", texto: mensajeDeError(e, "No se pudo abrir Miti.") });
    } finally {
      setAbriendoMiti(false);
    }
  };
  const hayRemotos = c.integrantes.some((i) => i.asistencia === "remoto");
  const respondieron = c.integrantes.filter((i) => i.respondio_en);
  // Con respuestas en la agenda actual, una nueva se confirma antes: la actual se elimina.
  const coordinarEnMiti = () => (c.miti.url && respondieron.length > 0 ? setDialogo("nueva") : abrirMiti());
  // En qué va la coordinación: sin agenda, esperando respuestas, toca elegir el horario en Miti
  // (todos respondieron, o el confirmado no le sirve a alguien) o ya está acordado.
  const etapa: "crear" | "respuestas" | "horario" | "acordado" = !c.miti.url
    ? "crear"
    : c.todos_coordinados || estado?.codigo === "coordinado" || estado?.codigo === "agendado"
      ? "acordado"
      : c.miti.estado === "CERRADA" || (c.integrantes.length > 0 && respondieron.length === c.integrantes.length)
        ? "horario"
        : "respuestas";

  const onRevisar = () =>
    revisar.mutate(it.id, {
      onSuccess: (r) => {
        if (r.error_sar) setAviso({ tono: "warning", texto: r.error_sar });
        else if (r.en_sar) setAviso({ tono: "success", texto: `SAR la tiene: ${r.en_sar.sala.nombre}, ${cuando(r.en_sar.inicio, r.en_sar.termino)}.` });
        else if (d) setAviso({ tono: "warning", texto: "En SAR ya no existe esta reserva: quedó como cancelada. Hay que volver a coordinar." });
      },
    });

  return (
    <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">Fecha del examen</Typography>
        {estado && <EstadoChip label={estado.label} tono={ESTADO_TONO[estado.codigo]} denso />}
      </Stack>

      {agendada && d && (
        <Stack direction="row" flexWrap="wrap" spacing={3} useFlexGap sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Cuándo
            </Typography>
            <Typography variant="body2">{texto}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Sala
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" fontWeight={600}>
                {d.sala}
              </Typography>
              {d.sar_url && (
                <Button size="small" variant="text" href={d.sar_url} target="_blank" rel="noreferrer" endIcon={<AbrirIcon fontSize="small" />}>
                  Ver en SAR
                </Button>
              )}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Modalidad
            </Typography>
            <Typography variant="body2">
              {d.modalidad_display} · {d.comision_presencial} presencial{d.comision_presencial === 1 ? "" : "es"}
              {d.comision_remota ? `, ${d.comision_remota} remoto${d.comision_remota === 1 ? "" : "s"}` : ""}
            </Typography>
          </Box>
        </Stack>
      )}
      {!agendada && d && gestion && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tenía {d.sala || "sala"} el {texto}; se canceló.
        </Typography>
      )}

      {gestion && (
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Coordinación{c.fuente === "miti" ? " en Miti" : ""}
            {c.fuente === "miti" && c.miti.estado ? ` · agenda ${c.miti.estado === "CERRADA" ? "cerrada" : "abierta"}` : ""}
          </Typography>
          <Typography variant="body2">
            {c.inicio && c.termino
              ? `${c.todos_coordinados ? "Acordado" : "Último horario propuesto"}: ${cuando(c.inicio, c.termino)}`
              : "Todavía no hay un horario acordado."}
          </Typography>
          <Integrantes c={c} it={it} editable={gestion && c.fuente === "miti" && c.miti.estado === "ABIERTA"} esCoordinador={esCoordinador} />
          {c.miti.horario_rechazado && !c.todos_coordinados && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
              No había salas el {cuando(c.miti.horario_rechazado, c.miti.horario_rechazado)}: reabran la agenda en Miti y acuerden otro
              horario.
            </Typography>
          )}
          {c.todos_coordinados && hayRemotos && !agendada && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Hay integrantes remotos: falta el enlace de la videollamada para agendar.
            </Typography>
          )}
        </Box>
      )}

      {gestion && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
          {/* El botón destacado va primero y guía a quien coordina (estudiante o coordinador): crear la
              agenda, seguir las respuestas, elegir el horario en Miti y, si hace falta, agendar en SAR. */}
          {gestion && c.miti.configurado && etapa === "crear" && (
            <Button size="small" variant="contained" startIcon={<MitiIcon />} onClick={coordinarEnMiti} disabled={abriendoMiti}>
              Coordinar en Miti
            </Button>
          )}
          {conMiti && etapa === "respuestas" && (
            <Button size="small" variant="contained" startIcon={<RevisarIcon />} onClick={() => sincronizar.mutate(it.id)} disabled={sincronizar.isPending}>
              {sincronizar.isPending ? "Leyendo Miti…" : "Actualizar desde Miti"}
            </Button>
          )}
          {c.miti.url && (
            <Button
              size="small"
              variant={agenda && etapa === "horario" ? "contained" : "text"}
              href={c.miti.url}
              target="_blank"
              rel="noreferrer"
              startIcon={agenda && etapa === "horario" ? <MitiIcon /> : undefined}
              endIcon={<AbrirIcon fontSize="small" />}
            >
              {agenda && etapa === "horario" ? "Elegir el horario en Miti" : "Ver la agenda en Miti"}
            </Button>
          )}
          {agenda && estado?.codigo === "coordinado" && (
            <Button size="small" variant="contained" startIcon={<AgendarIcon />} onClick={() => setDialogo("agendar")}>
              {agendada ? "Mover en SAR" : hayRemotos ? "Agregar enlace y agendar" : "Agendar en SAR"}
            </Button>
          )}
          {conMiti && etapa !== "respuestas" && (
            <Button size="small" variant="text" startIcon={<RevisarIcon />} onClick={() => sincronizar.mutate(it.id)} disabled={sincronizar.isPending}>
              {sincronizar.isPending ? "Leyendo Miti…" : "Actualizar desde Miti"}
            </Button>
          )}
          {gestion && c.miti.configurado && c.miti.url && estado?.codigo !== "agendado" && (
            <Button size="small" variant="text" startIcon={<MitiIcon />} onClick={coordinarEnMiti} disabled={abriendoMiti}>
              Nueva agenda en Miti
            </Button>
          )}
          {agenda && agendada && (
            <Button size="small" variant="text" color="error" startIcon={<CancelarIcon />} onClick={() => setDialogo("cancelar")}>
              Cancelar
            </Button>
          )}
          {d && (
            <Button size="small" variant="text" startIcon={<RevisarIcon />} onClick={onRevisar} disabled={revisar.isPending}>
              {revisar.isPending ? "Revisando…" : "Revisar en SAR"}
            </Button>
          )}
        </Stack>
      )}
      {sincronizar.isError && (
        <Alert severity="warning" sx={{ mt: 1.25 }}>
          {mensajeDeError(sincronizar.error, "No se pudo leer Miti.")}
        </Alert>
      )}
      {aviso && (
        <Alert severity={aviso.tono} sx={{ mt: 1.25 }} onClose={() => setAviso(null)}>
          {aviso.texto}
        </Alert>
      )}

      {dialogo === "agendar" && (
        <DialogoAgendar it={it} onClose={() => setDialogo(null)} onListo={(texto) => setAviso({ tono: "success", texto })} />
      )}
      {dialogo === "nueva" && (
        <DialogoNuevaAgenda respondieron={respondieron} onClose={() => setDialogo(null)} onConfirmar={abrirMiti} />
      )}
      {dialogo === "cancelar" && d && (
        <DialogoCancelar
          it={it}
          onClose={() => setDialogo(null)}
          onListo={(texto) => setAviso({ tono: "success", texto: `${texto} La coordinación vuelve a empezar.` })}
        />
      )}
    </Box>
  );
}
